import React, { useEffect, useRef } from 'react';

import { Text } from '@change/ds-core-ui/Text';

import { Translate } from '@change-corgi/core-react/i18n';
import { ClientRender } from '@change-corgi/core-react/ssr/render';
import { useTracking } from '@change-corgi/core-react/tracking';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { SECTION_ID_MAPPING } from '#src/app/pages/petitionDetails/shared/consts';
import { useSupporterVideos } from '#src/app/pages/petitionDetails/shared/hooks';
import { parseSourceInfoFromUrl } from '#src/app/shared/utils/petitionVideo';

import { CreateVideo } from './components/CreateVideo';
import { LazyVideoComponent } from './components/LazyVideoComponent';
import { ViewPetitionFeed } from './components/ViewPetitionFeed';

export const SupporterVoicesVideos: React.FC<EmptyProps> = () => {
	const {
		data: {
			petition: { id: petitionId, slug },
			supporterVoicesVideos,
		},
	} = usePetitionDetailsPageData();

	const {
		data: { currentlyPlaying, supporterVideos, showCreateVideo },
		actions: { setCurrentlyPlaying, removeVideoFromState },
	} = useSupporterVideos({ supporterVoicesVideos });

	const track = useTracking();
	const trackedPetitionIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (trackedPetitionIdRef.current === petitionId) return;
		if (supporterVideos.length === 0) return;
		trackedPetitionIdRef.current = petitionId;
		void track('petition_page_supporters_voice_view', {
			petition_id: petitionId,
			showSupporterVoices: supporterVoicesVideos.enabled,
		});
	}, [petitionId, supporterVideos.length, supporterVoicesVideos.enabled, track]);

	if (supporterVideos.length === 0) {
		return null;
	}

	return (
		<div id={SECTION_ID_MAPPING.SUPPORTER_VOICES_VIDEOS} className="flex flex-col gap-6">
			<Text as="h2" fontWeight="normal" size="x-large" variant="body">
				<Translate value="corgi.petition_gamma.videos.title" />
			</Text>

			<div className="relative max-w-full overflow-x-auto overflow-y-hidden pt-6" tabIndex={0}>
				<div className="flex flex-row gap-4">
					{supporterVideos.map((video) => {
						const sourceInfo = parseSourceInfoFromUrl(video.videoUrl);
						const poster = 'poster' in sourceInfo ? sourceInfo.poster : undefined;
						return (
							<div key={video.id} className="shrink-0">
								<LazyVideoComponent
									poster={poster}
									className="h-[320px] object-cover"
									video={video}
									currentlyPlaying={currentlyPlaying}
									setCurrentlyPlaying={setCurrentlyPlaying}
									removeVideoFromState={removeVideoFromState}
								/>
							</div>
						);
					})}
					{showCreateVideo && <CreateVideo />}
				</div>
			</div>

			<ClientRender>
				<ViewPetitionFeed petitionId={petitionId} slug={slug} />
			</ClientRender>
		</div>
	);
};
